import axios from 'axios';

// Auto-detect API base URL for seamless local (5000), Render (10000/public), and Vercel
const env = import.meta.env || {};
const fromEnv = (env.VITE_API_BASE_URL || '').trim();
// If env provides a relative URL like '/api', use it as-is (Vite proxy handles it)
const initialBase = fromEnv || (env.PROD ? 'https://kreativ-api.onrender.com/api' : '/api');

export let apiBaseURL = String(initialBase).replace(/\/+$/, '');
// For relative base URLs, apiOrigin should be empty (same origin)
export let apiOrigin = apiBaseURL.startsWith('/') ? '' : apiBaseURL.replace(/\/?api$/, '');

export const api = axios.create({
  baseURL: apiBaseURL,
  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'Cache-Control': 'no-cache' },
});

const DETECT_CACHE_KEY = 'api_base_cache';
let initPromise = null;

function withTimeout(promise, ms = 2000) {
  return new Promise((resolve, reject) => {
    const id = setTimeout(() => reject(new Error('timeout')), ms);
    promise
      .then((v) => { clearTimeout(id); resolve(v); })
      .catch((e) => { clearTimeout(id); reject(e); });
  });
}

async function probe(url) {
  try {
    const res = await withTimeout(fetch(`${String(url).replace(/\/+$/, '')}/health`, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-store',
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    }), env.PROD ? 2500 : 1500);
    return res && res.ok;
  } catch {
    return false;
  }
}

async function probeAuth(url) {
  try {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('auth:token') : null;
    if (!token) return false;
    const res = await withTimeout(fetch(`${String(url).replace(/\/+$/, '')}/users/me`, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-store',
      credentials: 'omit',
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    }), env.PROD ? 2500 : 1500);
    return res && res.ok;
  } catch {
    return false;
  }
}

async function detectApiBase() {
  // If using relative URL (Vite proxy), no detection needed
  if (fromEnv && fromEnv.startsWith('/')) {
    return fromEnv;
  }
  
  // If explicitly provided via env (absolute URL), use it
  if (fromEnv) {
    const hasToken = typeof localStorage !== 'undefined' && !!localStorage.getItem('auth:token');
    try {
      if (hasToken) {
        // Prefer env-provided base; if token doesn’t validate, clear it to avoid cross-env leakage
        const tokenValid = await probeAuth(fromEnv);
        if (tokenValid) {
          try { localStorage.setItem(DETECT_CACHE_KEY, fromEnv); } catch (e) { void e; }
          return fromEnv;
        }
        // Token mismatch: keep using fromEnv but drop the token
        try { localStorage.removeItem('auth:token'); } catch (e) { void e; }
        // Still cache env base if reachable
        if (await probe(fromEnv)) {
          try { localStorage.setItem(DETECT_CACHE_KEY, fromEnv); } catch (e) { void e; }
        }
        // If env base is not reachable, attempt host/locals as fallbacks in dev
        const isDev = !!env.DEV && !env.PROD;
        if (isDev && !(await probe(fromEnv))) {
          const hostLocal = `http://${(typeof window !== 'undefined' && window.location && window.location.hostname) ? window.location.hostname : 'localhost'}:5000/api`;
          const local = 'http://localhost:5000/api';
          const localAlt = 'http://127.0.0.1:5000/api';
          const remote = 'https://kreativ-api.onrender.com/api';
          if (await probe(hostLocal)) { try { localStorage.setItem(DETECT_CACHE_KEY, hostLocal); } catch (e) { void e; } return hostLocal; }
          if (await probe(local)) { try { localStorage.setItem(DETECT_CACHE_KEY, local); } catch (e) { void e; } return local; }
          if (await probe(localAlt)) { try { localStorage.setItem(DETECT_CACHE_KEY, localAlt); } catch (e) { void e; } return localAlt; }
          if (await probe(remote)) { try { localStorage.setItem(DETECT_CACHE_KEY, remote); } catch (e) { void e; } return remote; }
        }
        return fromEnv;
      } else {
        // No token: prefer env base if reachable; otherwise still honor env to be explicit
        if (await probe(fromEnv)) {
          try { localStorage.setItem(DETECT_CACHE_KEY, fromEnv); } catch (e) { void e; }
        } else {
          const isDev = !!env.DEV && !env.PROD;
          if (isDev) {
            const hostLocal = `http://${(typeof window !== 'undefined' && window.location && window.location.hostname) ? window.location.hostname : 'localhost'}:5000/api`;
            const local = 'http://localhost:5000/api';
            const localAlt = 'http://127.0.0.1:5000/api';
            const remote = 'https://kreativ-api.onrender.com/api';
            if (await probe(hostLocal)) { try { localStorage.setItem(DETECT_CACHE_KEY, hostLocal); } catch (e) { void e; } return hostLocal; }
            if (await probe(local)) { try { localStorage.setItem(DETECT_CACHE_KEY, local); } catch (e) { void e; } return local; }
            if (await probe(localAlt)) { try { localStorage.setItem(DETECT_CACHE_KEY, localAlt); } catch (e) { void e; } return localAlt; }
            if (await probe(remote)) { try { localStorage.setItem(DETECT_CACHE_KEY, remote); } catch (e) { void e; } return remote; }
          }
        }
        return fromEnv;
      }
    } catch (e) { void e; }
    // As a last resort, return the env-provided base without probing
    return fromEnv;
  }

  const isDev = !!env.DEV && !env.PROD;

  // In dev, strongly prefer local API to avoid token/env mismatches.
  if (isDev) {
    const local = 'http://localhost:5000/api';
    const localAlt = 'http://127.0.0.1:5000/api';
    // Preview sandboxes sometimes require using the current hostname (e.g., network IP)
    const hostLocal = `http://${(typeof window !== 'undefined' && window.location && window.location.hostname) ? window.location.hostname : 'localhost'}:5000/api`;
    // If we have a token, first choose the base that validates it
    const hasToken = typeof localStorage !== 'undefined' && !!localStorage.getItem('auth:token');
    if (hasToken) {
      if (await probeAuth(local)) {
        try { localStorage.setItem(DETECT_CACHE_KEY, local); } catch (e) { void e; }
        return local;
      }
      if (await probeAuth(localAlt)) {
        try { localStorage.setItem(DETECT_CACHE_KEY, localAlt); } catch (e) { void e; }
        return localAlt;
      }
      if (await probeAuth(hostLocal)) {
        try { localStorage.setItem(DETECT_CACHE_KEY, hostLocal); } catch (e) { void e; }
        return hostLocal;
      }
      const remote = 'https://kreativ-api.onrender.com/api';
      if (await probeAuth(remote)) {
        try { localStorage.setItem(DETECT_CACHE_KEY, remote); } catch (e) { void e; }
        return remote;
      }
      // Fall back to public probe if token doesn’t validate anywhere
    }
    if (await probe(local)) {
      try { localStorage.setItem(DETECT_CACHE_KEY, local); } catch (e) { void e; }
      return local;
    }
    if (await probe(localAlt)) {
      try { localStorage.setItem(DETECT_CACHE_KEY, localAlt); } catch (e) { void e; }
      return localAlt;
    }
    if (await probe(hostLocal)) {
      try { localStorage.setItem(DETECT_CACHE_KEY, hostLocal); } catch (e) { void e; }
      return hostLocal;
    }
    const remote = 'https://kreativ-api.onrender.com/api';
    if (await probe(remote)) {
      try { localStorage.setItem(DETECT_CACHE_KEY, remote); } catch (e) { void e; }
      return remote;
    }
    return apiBaseURL; // fallback to initial
  }

  // In prod, prefer cached if reachable, then probe known candidates
  try {
    const cached = typeof localStorage !== 'undefined' ? localStorage.getItem(DETECT_CACHE_KEY) : '';
    const hasToken = typeof localStorage !== 'undefined' && !!localStorage.getItem('auth:token');
    if (cached) {
      if (hasToken) {
        if (await probeAuth(cached)) return cached;
      } else if (await probe(cached)) {
        return cached;
      }
    }
  } catch (e) { void e; }

  const candidates = ['https://kreativ-api.onrender.com/api', 'http://localhost:5000/api', 'http://127.0.0.1:5000/api'];
  for (const url of candidates) {
    const hasToken = typeof localStorage !== 'undefined' && !!localStorage.getItem('auth:token');
    if (hasToken) {
      if (await probeAuth(url)) {
        try { localStorage.setItem(DETECT_CACHE_KEY, url); } catch (e) { void e; }
        return url;
      }
    } else if (await probe(url)) {
      try { localStorage.setItem(DETECT_CACHE_KEY, url); } catch (e) { void e; }
      return url;
    }
  }
  return apiBaseURL; // fallback to initial
}

async function ensureApiBaseReady() {
  if (!initPromise) {
    initPromise = (async () => {
      try {
        const detected = await detectApiBase();
        const normalized = String(detected).replace(/\/+$/, '');
        if (normalized && normalized !== apiBaseURL) {
          apiBaseURL = normalized;
          // For relative base URLs, apiOrigin should be empty (same origin)
          apiOrigin = normalized.startsWith('/') ? '' : normalized.replace(/\/?api$/, '');
          api.defaults.baseURL = apiBaseURL;
        }
      } catch (e) { void e; }
    })();
  }
  return initPromise;
}

// Initialize Authorization from local storage, if present
try {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('auth:token') : null;
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }
} catch (e) { void e; }

// Database mode helpers for seed/prod switching
const DB_MODE_KEY = 'kreatives-db-mode';
const SEED_SUBDOMAINS = ['demo', 'staging', 'test'];

// Check if current hostname is a demo subdomain
function isDemoSubdomain() {
  try {
    const host = window.location.hostname || '';
    const subdomain = host.split('.')[0].toLowerCase();
    return SEED_SUBDOMAINS.includes(subdomain);
  } catch { return false; }
}

// Check for demo query param
function hasDemoQueryParam() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('db_mode') === 'seed' || params.get('demo') === 'true';
  } catch { return false; }
}

export function getDbMode() {
  // Priority 1: Demo subdomain always returns seed
  if (isDemoSubdomain()) return 'seed';
  // Priority 2: Demo query param
  if (hasDemoQueryParam()) return 'seed';
  // Priority 3: localStorage setting
  try { return localStorage.getItem(DB_MODE_KEY) || 'production'; } catch { return 'production'; }
}

export function setDbMode(mode) {
  try {
    localStorage.setItem(DB_MODE_KEY, mode);
    window.dispatchEvent(new CustomEvent('db-mode-change', { detail: mode }));
  } catch { void 0; }
}

export function isOnDemoSite() {
  return isDemoSubdomain() || hasDemoQueryParam();
}

// Request interceptor to attach token consistently
api.interceptors.request.use(async (config) => {
  await ensureApiBaseReady();
  try {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('auth:token') : null;
    if (token) config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
  } catch (e) { void e; }
  // Add database mode header
  try {
    const dbMode = getDbMode();
    config.headers = { ...(config.headers || {}), 'X-Database-Mode': dbMode };
  } catch (e) { void e; }
  // Ensure no caching of sensitive endpoints
  config.headers = { ...(config.headers || {}), 'Cache-Control': 'no-cache' };
  return config;
});

// Response interceptor to handle 401 -> redirect to login with redirect back
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || '';
    const isMeEndpoint = /\/users\/me$/.test(url);
    // Background badge probes (notifications, chats unread) should NOT trigger global redirects
    const isBadgeProbe = /\/chats\/unread-count$/.test(url) || /\/notifications(\?|$)/.test(url);
    if (status === 401) {
      try {
        const current = window?.location?.pathname || '/';
        const isOnLogin = current.startsWith('/login');
        const isAuthCall = /\/users\/(login|register|password\/request)/.test(url);
        // Avoid redirect loops on login and allow auth endpoints to surface errors
        // Also, do not auto-redirect on /users/me to let pages handle inline auth errors
        if (!isOnLogin && !isAuthCall && !isMeEndpoint && !isBadgeProbe) {
          // Clear token if invalid
          localStorage.removeItem('auth:token');
          // Redirect to login preserving intended path
          const redirect = encodeURIComponent(current);
          window.location.assign(`/login?redirect=${redirect}`);
        }
      } catch (e) { void e; }
    }
    // Handle 404 on /users/me with "User not found" to recover from env/token mismatch
    else if (status === 404) {
      try {
        const msg = String(err?.response?.data?.error || '').toLowerCase();
        // Do not auto-redirect on /users/me; let UI show inline error
        if (!isMeEndpoint && /user not found/.test(msg)) {
          // Clear token and force re-login to bind a valid token to the active API base
          localStorage.removeItem('auth:token');
          const current = window?.location?.pathname || '/';
          const redirect = encodeURIComponent(current);
          window.location.assign(`/login?redirect=${redirect}&reason=user-not-found`);
        }
      } catch (e) { void e; }
    }
    return Promise.reject(err);
  }
);

export async function apiGet(path, params) {
  const res = await api.get(path, { params: { ...(params || {}), _ts: Date.now() }, headers: { 'Cache-Control': 'no-cache' } });
  return res.data;
}

export async function apiPost(path, body) {
  const res = await api.post(path, body);
  return res.data;
}

export async function apiPut(path, body) {
  const res = await api.put(path, body);
  return res.data;
}

export async function apiPatch(path, body) {
  const res = await api.patch(path, body);
  return res.data;
}

export async function apiDelete(path) {
  const res = await api.delete(path);
  return res.data;
}

// Optional: expose a way to force re-detection (e.g., after logout/login)
export async function refreshApiBaseDetection() {
  initPromise = null;
  await ensureApiBaseReady();
}
